import ComponentManager from '@/Management/ComponentManager';
import ControlFactory from '@/Factories/ControlFactory';
import MLStringBoxViewModelType from '@/ViewModels/Controls/MLStringBoxViewModel';
import MLStringBoxType from '@/Controls/MLStringBox';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import TextBoxViewModelType from '@/ViewModels/Controls/TextBoxViewModel';
import { FilterInfo } from '#/DefaultViewExtensionInterfaces';
import ResourceManager from '@/Management/ResourceManager';
import Utilities from '@/Utility/Utilities';

export default class MLStringBoxFactory extends ControlFactory {

    private MLStringBoxViewModel = ComponentManager.getViewModel<typeof MLStringBoxViewModelType>('MLStringBoxViewModel');
    private MLStringBox = ComponentManager.getControl<typeof MLStringBoxType>('MLStringBox');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModelType>('TextBoxViewModel');

    private technicalNameRootProperty = '';
    private technicalNameValueMLS = '';
    private technicalNameValueMLSForEach = '';

    /*
    * Das JQuery-Element des Controls
    */
    private jqueryControl: JQuery;

    /**
     * @override
     */
    public viewModel: MLStringBoxViewModelType;

    public constructor(private params) {
        super(params);
    }

    /**
     * @override
     */
    control: MLStringBoxType;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jqueryControl != null) {
            return this.jqueryControl;
        }

        this.viewModel = this.createViewModel();
        this.control = this.createControl();

        let tuple = this.control.getTuple();
        this.jqueryControl = tuple.control;

        this.addOverwritableToControl(this.jqueryControl, [this.technicalNameValueMLS, this.detailViewItem.name], 'REVERT_MLSTRING_BUTTON_TOOLTIP');
        this.registerDefaultValidation([this.technicalNameRootProperty, this.detailViewItem.name, this.technicalNameValueMLS, this.technicalNameValueMLSForEach]);
        this.registerCustomValidation([this.detailViewItem.name], this.viewModel.value, (value: string): boolean => {
            return Utilities.validateInputForInvalidCharacters(value);
        });

        return this.jqueryControl;
    }

    protected createViewModel() {
        this.validateTechName();

        this.context.genericObject.suppressNotificationFor(this.detailViewItem.name);

        let length = this.detailViewItem.length;
        if (isNaN(length) || length === 0) {
            length = 255;
        }

        let boxParams = {
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            parentLocator: this.locator,
            isDropdownFocusable: this.isFocusable,
            locator: this.technicalNameRootProperty,
            value: this.context.genericObject.getObservable(this.technicalNameValueMLS)
        };
        let textParams = {
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            isFocusable: this.isFocusable,
            invalidationText: this.invalidationText,
            onSubmit: this.params.onSubmit,
            maxLength: length,
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            })
        };

        // Sprachspezifisches Verhalten abschalten, wenn es ein Filter ist (CF-1744)
        if (!this.detailViewItem.useInFilter) {
            $.extend(boxParams, {
                isEachLanguageEqual: this.context.genericObject.getObservable(this.technicalNameValueMLSForEach),
                separatorString: this.context.genericObject.getObservable(this.technicalNameRootProperty + '.SeparatorString')
            });
        }
        else {
            $.extend(boxParams, {
                isEachLanguageEqual: true,
                separatorString: ResourceManager.getInstance().MLSTRING_SEPARATOR
            });
            $.extend(textParams, {
                value: this.context.genericObject.getObservable(this.detailViewItem.name)
            });
        }

        let vm = new this.MLStringBoxViewModel($.extend({}, boxParams, {
            textBox: new this.TextBoxViewModel(textParams)
        }));

        if (this.detailViewItem.useInFilter) {
            // Außerdem muss die Dropdown-Liste ausgeblendet werden
            vm.dropDownList.isVisible(false);
        }

        return vm;
    }

    protected createControl() {
        return new this.MLStringBox(this.viewModel);
    }

    private validateTechName() {
        this.technicalNameRootProperty = this.detailViewItem.name;

        if (this.detailViewItem.name.substring(this.detailViewItem.name.length - 6) === '.Value') {
            this.technicalNameRootProperty = this.detailViewItem.name.substring(0, this.detailViewItem.name.length - 6);
        }

        this.technicalNameValueMLS = this.technicalNameRootProperty + '.ValueMLS';
        this.technicalNameValueMLSForEach = this.technicalNameRootProperty + '.ForEachLanguageEquals';
    }

    // @Override
    public getAvailableOperators() {
        let result = [];

        result.push({ ergName: 'LIKE', value: 'LIKE' });

        return result;
    }

    // @Override
    public getValueForFilter() {
        if (this.viewModel && !this.viewModel.isDisposed) {
            const typedViewModel = this.viewModel as MLStringBoxViewModelType;

            let ret = {};
            ret[this.technicalNameValueMLS] = typedViewModel.textBox.value();
            return ret
        }
        return null;
    }

    // @Override
    public getFilterInfo(): FilterInfo {
        return {
            field: this.technicalNameValueMLS,
            value: this.viewModel?.textBox.value() || ''
        }
    }
}

ComponentManager.setFactory('MLStringBox', MLStringBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');